import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import NumberedId from '@/components/common/NumberedId';
import { dealAmountFor, dealProgressFor } from '@/lib/dashboard';
import { makeDealUrl } from '@/lib/dealLinks';
import { dealStatusView } from '@/lib/dealStatus';
import { formatMoney, formatOrgName } from '@/lib/format';
import type { DealOrderType, SearchBargain } from '@/types/deals';
import classes from './DealsTable.module.css';

interface DealsTableProps {
  readonly deals: readonly SearchBargain[];
  /** Сторона вкладки. Не задана у агента: он посредник и видит обе стороны. */
  readonly orderType?: DealOrderType;
  readonly isAgent: boolean;
}

// Список «Мои сделки» таблицей (образец b-deals): номер, товар, контрагент, маршрут, вес, цена, сумма по роли и
// ход сделки. Вся строка ведёт на сделку. На телефоне строки превращаются в блоки с подписями чисел.
export default function DealsTable({ deals, orderType, isAgent }: DealsTableProps) {
  const { t } = useTranslation();

  return (
    <table className={classes.table}>
      <thead>
        <tr>
          <th>{t('app.deals.table.no')}</th>
          <th>{t('app.deals.table.goods')}</th>
          <th>{t(isAgent ? 'app.deals.table.parties' : 'app.deals.table.counterparty')}</th>
          <th>{t('app.deals.table.route')}</th>
          <th className={classes.num}>{t('app.deals.table.weight')}</th>
          <th className={classes.num}>{t('app.deals.table.price')}</th>
          <th className={classes.num}>{t('app.deals.table.amount')}</th>
          <th>{t('app.deals.table.progress')}</th>
        </tr>
      </thead>
      <tbody>
        {deals.map((deal) => (
          <DealRow key={deal.id} deal={deal} orderType={orderType} isAgent={isAgent} />
        ))}
      </tbody>
    </table>
  );
}

function DealRow({ deal, orderType, isAgent }: { readonly deal: SearchBargain; readonly orderType?: DealOrderType; readonly isAgent: boolean }) {
  const { t } = useTranslation();
  const dealNumber = deal.specName ?? deal.contractNum;
  const amount = dealAmountFor(orderType, deal, isAgent);
  const progress = dealProgressFor(orderType, deal, isAgent);
  // Отменённую сделку называем статусом: этап у неё последний пройденный и сбил бы с толку.
  const stage = deal.status === 'CANCELED' ? t(dealStatusView(deal.status).labelKey) : progress.stage;
  const from = formatOrgName(deal.fromAddressTitle) ?? deal.fromAddress;
  const to = formatOrgName(deal.toAddressTitle) ?? deal.toAddress;
  const counterparty = orderType === 'SALE' ? deal.buyer : deal.seller;

  return (
    <tr className={classes.row}>
      <td>
        <Link to={makeDealUrl(deal.id ?? 0)} className={classes.link}>
          {dealNumber ? <NumberedId value={dealNumber} /> : `№${deal.id}`}
        </Link>
      </td>
      <td>{deal.goodsType ?? '—'}</td>
      <td>
        {isAgent ? (
          <>
            {formatOrgName(deal.seller?.name) ?? '—'}
            <span className={classes.soft}>→ {formatOrgName(deal.buyer?.name) ?? '—'}</span>
          </>
        ) : (
          (formatOrgName(counterparty?.name) ?? '—')
        )}
      </td>
      <td>
        {from ?? '—'}
        {to ? <span className={classes.soft}>→ {to}</span> : null}
      </td>
      <td className={classes.num} data-label={t('app.deals.table.weight')}>
        {deal.plannedWeight != null ? formatMoney(deal.plannedWeight) : '—'}
      </td>
      <td className={classes.num} data-label={t('app.deals.table.price')}>
        {deal.price != null ? formatMoney(deal.price) : '—'}
      </td>
      <td className={`${classes.num} ${classes.sum}`} data-label={t('app.deals.table.amount')}>
        {amount != null ? formatMoney(amount) : '—'}
      </td>
      <td>
        {progress.stepLabel ? <span className={classes.step}>{progress.stepLabel}</span> : null}
        {progress.stepLabel && stage ? ' ' : null}
        {stage}
        {progress.lateDays ? (
          <span className={classes.late}>{t('app.deals.table.lateDays', { count: progress.lateDays })}</span>
        ) : progress.mine ? (
          <span className={classes.mine}>{t('app.deals.stepper.yourTurn')}</span>
        ) : null}
      </td>
    </tr>
  );
}
